import Head from 'next/head'
import { Router } from 'next/router'
import { FC, useEffect, useState } from 'react'
import { isSSR } from '../utils'
import { observeClsAttribution, resetClsAttribution } from './clsAttribution'

declare global {
  interface Window {
    LUX: {
      addData: (key: string, value: string | number | null) => void
      auto?: boolean
      debug?: boolean
      forceSample: () => void
      init: () => void
      label?: string
      samplerate?: number
      send: () => void
    }
  }
}

type SpeedCurveProps = {
  luxId: string | number
}

/**
 * Handle start of a route change.
 */
const onRouteChangeStart = () => {
  window.LUX.label = 'Other'
  window.LUX.init()
  resetClsAttribution()
}

/**
 * Handle end of a route change.
 */
const onRouteChangeComplete = () => {
  window.LUX.send()
  // eslint-disable-next-line no-console
  console.log(
    '%cSend label speedcurve:',
    'font-weight: bold; color: blue',
    window.LUX.label
  )
}

const SpeedCurve: FC<SpeedCurveProps> = ({ luxId }) => {
  const [loaded, setLoaded] = useState<boolean>(false)
  useEffect(() => {
    let stopClsAttribution: () => void
    if (loaded) {
      window.LUX.samplerate = 100

      Router.events.on('routeChangeStart', onRouteChangeStart)
      Router.events.on('routeChangeComplete', onRouteChangeComplete)
      stopClsAttribution = observeClsAttribution()
    }

    return () => {
      Router.events.off('routeChangeStart', onRouteChangeStart)
      Router.events.off('routeChangeComplete', onRouteChangeComplete)
      stopClsAttribution?.()
    }
  }, [loaded])

  useEffect(() => {
    if (window.LUX) {
      setLoaded(true)
    }
  }, [!isSSR && window.LUX])

  return (
    <Head>
      <script
        key="speedcurve-snippet"
        dangerouslySetInnerHTML={{
          __html:
            'LUX=(function(){var a=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMarks)?LUX.gaMarks:[]);var d=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMeasures)?LUX.gaMeasures:[]);var j="LUX_start";var k=window.performance;var l=("undefined"!==typeof(LUX)&&LUX.ns?LUX.ns:(Date.now?Date.now():+(new Date())));if(k&&k.timing&&k.timing.navigationStart){l=k.timing.navigationStart}function f(){if(k&&k.now){return k.now()}var o=Date.now?Date.now():+(new Date());return o-l}function b(n){if(k){if(k.mark){return k.mark(n)}else{if(k.webkitMark){return k.webkitMark(n)}}}a.push({name:n,entryType:"mark",startTime:f(),duration:0});return}function m(p,t,n){if("undefined"===typeof(t)&&h(j)){t=j}if(k){if(k.measure){if(t){if(n){return k.measure(p,t,n)}else{return k.measure(p,t)}}else{return k.measure(p)}}else{if(k.webkitMeasure){return k.webkitMeasure(p,t,n)}}}var r=0,o=f();if(t){var s=h(t);if(s){r=s.startTime}else{if(k&&k.timing&&k.timing[t]){r=k.timing[t]-k.timing.navigationStart}else{return}}}if(n){var q=h(n);if(q){o=q.startTime}else{if(k&&k.timing&&k.timing[n]){o=k.timing[n]-k.timing.navigationStart}else{return}}}d.push({name:p,entryType:"measure",startTime:r,duration:(o-r)});return}function h(n){return c(n,g())}function c(p,o){for(i=o.length-1;i>=0;i--){var n=o[i];if(p===n.name){return n}}return undefined}function g(){if(k){if(k.getEntriesByType){return k.getEntriesByType("mark")}else{if(k.webkitGetEntriesByType){return k.webkitGetEntriesByType("mark")}}}return a}return{mark:b,measure:m,gaMarks:a,gaMeasures:d}})();LUX.ns=(Date.now?Date.now():+(new Date()));LUX.ac=[];LUX.cmd=function(a){LUX.ac.push(a)};LUX.init=function(){LUX.cmd(["init"])};LUX.send=function(){LUX.cmd(["send"])};LUX.addData=function(a,b){LUX.cmd(["addData",a,b])};LUX_ae=[];window.addEventListener("error",function(a){LUX_ae.push(a)});LUX_al=[];if("function"===typeof(PerformanceObserver)&&"function"===typeof(PerformanceLongTaskTiming)){var LongTaskObserver=new PerformanceObserver(function(c){var b=c.getEntries();for(var a=0;a<b.length;a++){var d=b[a];LUX_al.push(d)}});try{LongTaskObserver.observe({type:["longtask"]})}catch(e){}};'
        }}
      />
      <script
        defer
        key="speedcurve-lux"
        src={`https://cdn.speedcurve.com/js/lux.js?id=${luxId}`}
        crossOrigin="anonymous"
      />
    </Head>
  )
}

export default SpeedCurve
